import { TabConfiguration, ColumnConfig } from './configBuilder';
import {
  PageConfig,
  ColumnConfig as TableColumnConfig,
  FilterConfiguration,
  FilterGroup,
} from 'components/LMA/types';

/**
 * Convert Sitecore column configuration to table column configuration
 * Adds system columns: expand (for expandable rows) and lock (for record locking)
 */
export const convertToTableColumns = (columns: ColumnConfig[]): TableColumnConfig[] => {
  const systemColumns: TableColumnConfig[] = [
    {
      id: 'expand',
      label: '',
      accessor: 'expand',
      sortable: false,
      width: '50px',
      align: 'left' as const,
    },
  ];

  const dataColumns: TableColumnConfig[] = columns.map((col) => ({
    ...col,
    id: col.id,
    label: col.label,
    accessor: col.field,
    sortable: col.sortable,
    width: col.width?.toString(),
    maxWidth: '300px',
    align: 'left' as const,
  }));

  // Lock column (added at the end, conditionally hidden via hideLockColumn prop)
  const lockColumn: TableColumnConfig = {
    id: 'lock',
    label: 'Lock',
    accessor: 'lock',
    sortable: false,
    width: '80px',
    align: 'center' as const,
  };

  // Patient Type column (shows ChildCare icon for minor patients)
  const patientTypeColumn: TableColumnConfig = {
    id: 'patientType',
    label: '',
    accessor: 'patientType',
    sortable: false,
    width: '50px',
    align: 'center' as const,
  };

  return [...systemColumns, ...dataColumns, lockColumn, patientTypeColumn];
};

/**
 * Convert Sitecore filter configuration to filter panel configuration
 */
export const convertToFilterConfig = (tabConfig: TabConfiguration | null): FilterConfiguration => {
  const filterGroups: FilterGroup[] = [];

  if (!tabConfig) {
    return {
      enabled: true,
      filterLabel: 'Filters',
      applyButtonText: 'Apply Filters',
      clearButtonText: 'Clear All',
      filterGroups: [],
    };
  }

  // Basic Search Filters Group
  const basicFilters = tabConfig.filters.filter((f) => f.type === 'text' || f.type === 'select');

  if (basicFilters.length > 0) {
    const fields = basicFilters.map((filter, index) => {
      // Use filter.id as the field mapping to ReferralData property
      // filter.id comes from FilterKey in Sitecore, which should match ReferralData property name
      const fieldMapping = filter.id;

      if (filter.type === 'text') {
        return {
          id: filter.id,
          label: filter.label,
          type: 'text' as const,
          enabled: true,
          displayOrder: index + 1,
          placeholder: `Enter ${filter.label}`,
          field: fieldMapping, // Maps to ReferralData property
        };
      } else {
        return {
          id: filter.id,
          label: filter.label,
          type: 'dropdown' as const,
          enabled: true,
          displayOrder: index + 1,
          placeholder: `Select ${filter.label}`,
          options: filter.options || [], // Options populated from data
          field: fieldMapping, // Maps to ReferralData property
          multiple: filter.multiple, // Use filter.multiple from config
          searchable: filter.multiple, // Enable search for multi-select dropdowns
          apiEndpoint: filter.apiEndpoint, // Copy API endpoint if present
          apiConfig: filter.apiConfig, // Copy API config if present
        };
      }
    });

    filterGroups.push({
      id: 'search-filters',
      title: 'Search Filters',
      type: 'card',
      displayOrder: 1,
      enabled: true,
      collapsible: false,
      fields,
    });
  }

  // Date Filters Group
  const dateFilters = tabConfig.filters.filter((f) => f.type === 'date' || f.type === 'dateRange');

  if (dateFilters.length > 0) {
    const fields = dateFilters.map((filter, index) => {
      // Use filter.id as the field mapping to ReferralData property
      const fieldMapping = filter.id;

      if (filter.type === 'dateRange') {
        return {
          id: filter.id,
          label: filter.label,
          type: 'dateRange' as const,
          enabled: true,
          displayOrder: index + 1,
          options: [
            { value: 'today', label: 'Today' },
            { value: 'last7Days', label: 'Last 7 Days' },
            { value: 'last30Days', label: 'Last 30 Days' },
            { value: 'custom', label: 'Custom Range' },
          ],
          field: fieldMapping, // Maps to ReferralData property (usually 'dateTimestamp')
        };
      } else {
        return {
          id: filter.id,
          label: filter.label,
          type: 'date' as const,
          enabled: true,
          displayOrder: index + 1,
          field: fieldMapping, // Maps to ReferralData property
        };
      }
    });

    filterGroups.push({
      id: 'date-filters',
      title: 'Date Filters',
      type: 'card',
      displayOrder: 2,
      enabled: true,
      collapsible: false,
      fields,
    });
  }

  // Lead Type and Dedupe Status Filters Group (Combined in one card)
  if (tabConfig.leadStatusFilters.length > 0 || tabConfig.dedupeStatusFilters.length > 0) {
    const checkboxGroups = [];

    // Add Lead Type checkbox group
    if (tabConfig.leadStatusFilters.length > 0) {
      checkboxGroups.push({
        id: 'leadType',
        title: tabConfig.leadTypeText || 'Lead Type',
        enabled: true,
        displayOrder: 2,
        field: 'leadType',
        options: tabConfig.leadStatusFilters.map((status) => ({
          value: status.value,
          label: status.label,
          color: status.color,
          bgColor: status.bgColor,
          borderColor: status.color,
        })),
      });
    }

    // Add Dedupe Status checkbox group
    if (tabConfig.dedupeStatusFilters.length > 0) {
      checkboxGroups.push({
        id: 'dedupeStatus',
        title: tabConfig.dedupeStatusText || 'Dedupe Status',
        enabled: true,
        displayOrder: 1,
        field: 'dedupeStatus',
        options: tabConfig.dedupeStatusFilters.map((status) => ({
          value: status.value,
          label: status.label,
          color: status.color,
          bgColor: status.bgColor,
          borderColor: status.color,
        })),
      });
    }

    filterGroups.push({
      id: 'lead-and-dedupe-status',
      title: 'Lead Type & Dedupe Status',
      type: 'checkbox-card',
      displayOrder: 3,
      enabled: true,
      collapsible: false,
      checkboxGroups,
      layout: {
        type: 'grid',
        minWidth: '250px',
        maxWidth: '600px',
        columns: 2,
      },
    });
  }

  // Match Percentage Slider
  const matchFilter = tabConfig.filters.find((f) => f.type === 'slider');
  if (matchFilter) {
    const minValue = matchFilter.min ?? 0;
    const maxValue = matchFilter.max ?? 100;

    filterGroups.push({
      id: 'match-percentage',
      title: 'Match Percentage',
      type: 'card',
      displayOrder: 4,
      enabled: true,
      collapsible: false,
      fields: [
        {
          id: matchFilter.id,
          label: matchFilter.label,
          type: 'slider' as const,
          enabled: true,
          displayOrder: 1,
          min: minValue,
          max: maxValue,
          step: 1,
          defaultValue: [minValue, maxValue] as [number, number],
          showValue: true,
          field: matchFilter.id, // Maps to ReferralData property (usually 'matchPercentage')
          fields: {
            minField: 'matchPercentageMin',
            maxField: 'matchPercentageMax',
          },
        },
      ],
    });
  }

  return {
    enabled: true,
    filterLabel: 'Filters',
    applyButtonText: 'Apply Filters',
    clearButtonText: 'Clear All',
    filterGroups,
  };
};

/**
 * Build a complete PageConfig from TabConfiguration for backward compatibility
 */
export const buildPageConfig = (tabConfig: TabConfiguration | null): PageConfig => {
  // Default config structure
  const defaultConfig: PageConfig = {
    filterConfig: {
      enabled: true,
      filterLabel: 'Filters',
      applyButtonText: 'Apply Filters',
      clearButtonText: 'Clear All',
      filterGroups: [],
    },
    enableFilters: true,
    enableSelection: true,
    enableExpandableRows: true,
    columns: [],
    tabs: [
      { label: 'Referrals', dataKey: 'referrals', value: 0 },
      { label: 'Non-Referrals', dataKey: 'nonReferrals', value: 1 },
      { label: 'Processed to CC', dataKey: 'processedReferrals', value: 2 },
    ],
  };

  if (!tabConfig) {
    return defaultConfig;
  }

  // Convert Sitecore columns to table columns
  const tableColumns = convertToTableColumns(tabConfig.columns);

  // Build filter configuration
  const filterConfig = convertToFilterConfig(tabConfig);

  return {
    ...defaultConfig,
    columns: tableColumns,
    filterConfig,
    noDataMessage: tabConfig.noResultsText,
  };
};

/**
 * Get expanded view field configuration with header labels
 */
export const getExpandedViewConfig = (
  tabConfig: TabConfiguration | null,
  headerLabels?: {
    personalInfoHeaderText?: string;
    demographicsHeaderText?: string;
    caregiverInfoHeaderText?: string;
    trackingParamsHeaderText?: string;
    dedupAddParamsHeaderText?: string;
    commentsHeaderText?: string;
  }
) => {
  if (!tabConfig) {
    return {
      personal: [],
      demographics: [],
      caregiverInfo: [],
      trackingParams: [],
      dedupAddParams: [],
      personalInfoHeaderText: headerLabels?.personalInfoHeaderText || 'Personal Information',
      demographicsHeaderText: headerLabels?.demographicsHeaderText || 'Demographics',
      caregiverInfoHeaderText: headerLabels?.caregiverInfoHeaderText || 'Caregiver Information',
      trackingParamsHeaderText: headerLabels?.trackingParamsHeaderText || 'Tracking Parameters',
      dedupAddParamsHeaderText:
        headerLabels?.dedupAddParamsHeaderText || 'Deduplication Additional Parameters',
      commentsHeaderText: headerLabels?.commentsHeaderText || 'Comments',
      viewDetails: {
        href: '',
        text: '',
      },
      sendToCC: '',
    };
  }

  return {
    ...tabConfig.expandedFields,
    personalInfoHeaderText: headerLabels?.personalInfoHeaderText || 'Personal Information',
    demographicsHeaderText: headerLabels?.demographicsHeaderText || 'Demographics',
    caregiverInfoHeaderText: headerLabels?.caregiverInfoHeaderText || 'Caregiver Information',
    trackingParamsHeaderText: headerLabels?.trackingParamsHeaderText || 'Tracking Parameters',
    dedupAddParamsHeaderText:
      headerLabels?.dedupAddParamsHeaderText || 'Deduplication Additional Parameters',
    commentsHeaderText: headerLabels?.commentsHeaderText || 'Comments',
    viewDetails: tabConfig.viewDetails,
    sendToCC: tabConfig.sendToCC,
  };
};
